import {
    BankOutlined,
    DeleteOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    EyeOutlined,
    MailOutlined,
    PlusOutlined,
    SearchOutlined,
    TeamOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Dropdown,
    Input,
    Modal,
    Row,
    Select,
    Skeleton,
    Space,
    Switch,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { API_URL, TOKEN } from "../../variables/constants";
import AddMembres from "./AddMembres";

const { confirm } = Modal;
const { Title, Text } = Typography;
const { Option } = Select;

/* ── Couleurs par type ── */
const TYPE_META = {
    ASSURANCE: { color: "#1677ff", bg: "#e6f4ff", label: "Assureur" },
    PARTENAIRE: { color: "#52c41a", bg: "#f6ffed", label: "Partenaire" },
};

const AVATAR_COLORS = [
    "#1677ff", "#52c41a", "#fa8c16", "#722ed1",
    "#eb2f96", "#13c2c2", "#f5222d", "#2f54eb",
];

const avatarColor = (str = "") =>
    AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];

/* ── Carte d'un membre ── */
const MembreCard = ({ membre, onView, onEdit, onDelete, onToggleActive }) => {
    const meta = TYPE_META[membre._type] || TYPE_META.PARTENAIRE;
    const initials = (membre.raisonSociale || "?")
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");

    const menuItems = [
        {
            key: "view",
            icon: <EyeOutlined />,
            label: "Voir les détails",
            onClick: () => onView(membre),
        },
        {
            key: "edit",
            icon: <EditOutlined />,
            label: "Modifier",
            onClick: () => onEdit(membre),
        },
        {
            key: "delete",
            icon: <DeleteOutlined />,
            label: "Supprimer",
            danger: true,
            onClick: () => onDelete(membre.organismeId),
        },
    ];

    return (
        <Card
            hoverable
            styles={{
                body: { padding: "20px" },
            }}
            style={{
                borderRadius: 12,
                border: "1px solid #f0f0f0",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                height: "100%",
                transition: "box-shadow 0.2s, transform 0.2s",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.12)";
                e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)";
                e.currentTarget.style.transform = "translateY(0)";
            }}
        >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Space size={12} align="start">
                    <Avatar
                        size={48}
                        style={{
                            backgroundColor: avatarColor(membre.raisonSociale || ""),
                            fontSize: 18,
                            fontWeight: 700,
                            flexShrink: 0,
                        }}
                    >
                        {initials}
                    </Avatar>
                    <div style={{ minWidth: 0 }}>
                        <Text
                            strong
                            style={{
                                fontSize: 15,
                                display: "block",
                                cursor: "pointer",
                                color: "#262626",
                                lineHeight: "1.3",
                            }}
                            ellipsis={{ tooltip: membre.raisonSociale }}
                            onClick={() => onView(membre)}
                        >
                            {membre.raisonSociale}
                        </Text>
                        <Tag
                            style={{
                                marginTop: 4,
                                color: meta.color,
                                background: meta.bg,
                                border: `1px solid ${meta.color}30`,
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                            }}
                        >
                            {meta.label}
                        </Tag>
                    </div>
                </Space>

                <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
                    <Button
                        type="text"
                        size="small"
                        style={{ color: "#8c8c8c", marginLeft: 8 }}
                        icon={<span style={{ fontSize: 18, lineHeight: 1 }}>⋮</span>}
                    />
                </Dropdown>
            </div>

            <div
                style={{
                    marginTop: 16,
                    paddingTop: 14,
                    borderTop: "1px solid #f5f5f5",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                }}
            >
                {membre.code && (
                    <Space size={6}>
                        <BankOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Code :{" "}
                            <Text style={{ fontSize: 13, fontWeight: 600 }}>{membre.code}</Text>
                        </Text>
                    </Space>
                )}
                {membre.email && (
                    <Space size={6} style={{ overflow: "hidden" }}>
                        <MailOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                        <Text
                            type="secondary"
                            style={{ fontSize: 13 }}
                            ellipsis={{ tooltip: membre.email }}
                        >
                            {membre.email}
                        </Text>
                    </Space>
                )}
                {membre.responsable && (
                    <Space size={6}>
                        <UserOutlined style={{ color: "#8c8c8c", fontSize: 13 }} />
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {membre.responsable}
                        </Text>
                    </Space>
                )}
            </div>

            <div
                style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: "1px solid #f5f5f5",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontSize: 13 }}
                    onClick={() => onView(membre)}
                >
                    Voir les détails →
                </Button>
                <Tooltip title={membre.active ? "Retirer du Pool" : "Réintégrer au Pool"}>
                    <Switch
                        size="small"
                        checked={membre.active}
                        checkedChildren="Actif"
                        unCheckedChildren="Inactif"
                        onChange={() => onToggleActive(membre)}
                    />
                </Tooltip>
            </div>
        </Card>
    );
};

/* ── Skeleton card ── */
const SkeletonCard = () => (
    <Card style={{ borderRadius: 12, border: "1px solid #f0f0f0" }}>
        <Skeleton avatar active paragraph={{ rows: 3 }} />
    </Card>
);

/* ── Stat card ── */
const StatCard = ({ icon, label, value, color, bg }) => (
    <Card
        style={{
            borderRadius: 12,
            border: "none",
            background: bg,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        styles={{ body: { padding: "20px 24px" } }}
    >
        <Space size={16} align="center">
            <div
                style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: `${color}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    color,
                }}
            >
                {icon}
            </div>
            <div>
                <Text type="secondary" style={{ fontSize: 13 }}>
                    {label}
                </Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#262626", lineHeight: 1.2 }}>
                    {value}
                </div>
            </div>
        </Space>
    </Card>
);

/* ══════════════════════════════════════════════════════ */
const AllMembres = () => {
    const [assurances, setAssurances] = useState([]);
    const [partenaires, setPartenaires] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedMembre, setSelectedMembre] = useState(null);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("ALL");
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        const headers = { Authorization: `Bearer ${TOKEN}` };

        Promise.all([
            axios.get(`${API_URL}/api/organisme/assurance`, { headers }),
            axios.get(`${API_URL}/api/organisme/partenaire`, { headers }),
        ])
            .then(([resA, resP]) => {
                setAssurances(resA.data);
                setPartenaires(resP.data);
            })
            .catch(() => toast.error("Erreur lors du chargement des membres"))
            .finally(() => setLoading(false));
    }, []);

    const allMembers = useMemo(() => [
        ...assurances.map((m) => ({ ...m, _type: "ASSURANCE", key: m.organismeId })),
        ...partenaires.map((m) => ({ ...m, _type: "PARTENAIRE", key: m.organismeId })),
    ], [assurances, partenaires]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return allMembers.filter((m) => {
            const matchType = typeFilter === "ALL" || m._type === typeFilter;
            const matchSearch =
                !q ||
                (m.raisonSociale || "").toLowerCase().includes(q) ||
                (m.code || "").toLowerCase().includes(q) ||
                (m.email || "").toLowerCase().includes(q) ||
                (m.responsable || "").toLowerCase().includes(q);
            return matchType && matchSearch;
        });
    }, [allMembers, search, typeFilter]);

    const showDeleteConfirm = (organismeId) => {
        confirm({
            title: "Supprimer ce membre ?",
            icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
            content: "Cette action est irréversible. Toutes les données associées seront perdues.",
            okText: "Supprimer",
            okType: "danger",
            cancelText: "Annuler",
            onOk() {
                axios
                    .delete(`${API_URL}/api/organisme/${organismeId}`, {
                        headers: { Authorization: `Bearer ${TOKEN}` },
                    })
                    .then(() => {
                        setAssurances((prev) => prev.filter((i) => i.organismeId !== organismeId));
                        setPartenaires((prev) => prev.filter((i) => i.organismeId !== organismeId));
                        toast.success("Membre supprimé avec succès");
                    })
                    .catch(() => toast.error("Erreur lors de la suppression"));
            },
        });
    };

    const handleToggleActive = (membre) => {
        const isCurrentlyActive = membre.active;
        const actionLabel = isCurrentlyActive ? "retirer du Pool" : "réintégrer au Pool";
        confirm({
            title: `${isCurrentlyActive ? "Retirer" : "Réintégrer"} cette compagnie ${isCurrentlyActive ? "du" : "au"} Pool ?`,
            icon: <ExclamationCircleFilled style={{ color: isCurrentlyActive ? "#ff4d4f" : "#1677ff" }} />,
            content: isCurrentlyActive
                ? "La compagnie ne sera plus active pour la production. Cette action est réversible."
                : "La compagnie sera de nouveau active pour la production.",
            okText: isCurrentlyActive ? "Retirer" : "Réintégrer",
            okType: isCurrentlyActive ? "danger" : "primary",
            cancelText: "Annuler",
            onOk() {
                return axios
                    .patch(`${API_URL}/api/organisme/${membre.organismeId}/toggle-active`, null, {
                        headers: { Authorization: `Bearer ${TOKEN}` },
                    })
                    .then((res) => {
                        const updated = res.data;
                        const updater = (prev) =>
                            prev.map((m) => m.organismeId === updated.organismeId ? { ...m, active: updated.active } : m);
                        if (membre._type === "ASSURANCE") setAssurances(updater);
                        else setPartenaires(updater);
                        toast.success(
                            `Compagnie ${updated.active ? "réintégrée" : "retirée"} du Pool avec succès`
                        );
                    })
                    .catch(() => toast.error(`Erreur lors de l'action`));
            },
        });
    };

    const handleShowModal = (membre = null) => {
        setSelectedMembre(membre);
        setEditMode(!!membre);
        setShowModal(true);
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"MEMBRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div
                            className="container-xxl flex-grow-1 container-p-y"
                            style={{ backgroundColor: "#f8f9fb", minHeight: "100vh" }}
                        >
                            {/* ── En-tête ── */}
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 24,
                                }}
                            >
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#262626" }}>
                                        Membres & Partenaires
                                    </Title>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        Gestion des compagnies membres et partenaires du Pool
                                    </Text>
                                </div>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size="large"
                                    onClick={() => handleShowModal()}
                                    style={{ borderRadius: 8, fontWeight: 600 }}
                                >
                                    Nouveau membre
                                </Button>
                            </div>

                            {/* ── Stat cards ── */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={8}>
                                    <StatCard
                                        icon={<TeamOutlined />}
                                        label="Total membres"
                                        value={loading ? "—" : allMembers.length}
                                        color="#1677ff"
                                        bg="#fff"
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <StatCard
                                        icon={<BankOutlined />}
                                        label="Assureurs"
                                        value={loading ? "—" : assurances.length}
                                        color="#1677ff"
                                        bg="#fff"
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <StatCard
                                        icon={<TeamOutlined />}
                                        label="Partenaires"
                                        value={loading ? "—" : partenaires.length}
                                        color="#52c41a"
                                        bg="#fff"
                                    />
                                </Col>
                            </Row>

                            {/* ── Barre de recherche + filtre ── */}
                            <Card
                                style={{
                                    borderRadius: 12,
                                    border: "1px solid #f0f0f0",
                                    marginBottom: 20,
                                    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                                }}
                                styles={{ body: { padding: "16px 20px" } }}
                            >
                                <Row gutter={12} align="middle">
                                    <Col flex="1">
                                        <Input
                                            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                                            placeholder="Rechercher par nom, code, email, responsable…"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            allowClear
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Col>
                                    <Col>
                                        <Select
                                            value={typeFilter}
                                            onChange={setTypeFilter}
                                            style={{ width: 160, borderRadius: 8 }}
                                        >
                                            <Option value="ALL">Tous les types</Option>
                                            <Option value="ASSURANCE">Assureurs</Option>
                                            <Option value="PARTENAIRE">Partenaires</Option>
                                        </Select>
                                    </Col>
                                    <Col>
                                        <Text type="secondary" style={{ fontSize: 13 }}>
                                            {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
                                        </Text>
                                    </Col>
                                </Row>
                            </Card>

                            {/* ── Grille de cartes ── */}
                            {loading ? (
                                <Row gutter={[16, 16]}>
                                    {Array.from({ length: 6 }).map((_, i) => (
                                        <Col key={i} xs={24} sm={12} lg={8} xl={6}>
                                            <SkeletonCard />
                                        </Col>
                                    ))}
                                </Row>
                            ) : filtered.length === 0 ? (
                                <Card
                                    style={{
                                        borderRadius: 12,
                                        textAlign: "center",
                                        padding: "48px 0",
                                        border: "1px dashed #d9d9d9",
                                    }}
                                >
                                    <TeamOutlined style={{ fontSize: 40, color: "#bfbfbf", marginBottom: 12 }} />
                                    <div>
                                        <Text type="secondary">Aucun membre trouvé</Text>
                                    </div>
                                </Card>
                            ) : (
                                <Row gutter={[16, 16]}>
                                    {filtered.map((membre) => (
                                        <Col key={membre.organismeId} xs={24} sm={12} lg={8} xl={6}>
                                            <MembreCard
                                                membre={membre}
                                                onView={(m) => navigate(`/membre/${m.code}`)}
                                                onEdit={(m) => handleShowModal(m)}
                                                onDelete={showDeleteConfirm}
                                                onToggleActive={handleToggleActive}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>

            {showModal && (
                <AddMembres
                    show={showModal}
                    handleClose={() => {
                        setShowModal(false);
                        setSelectedMembre(null);
                    }}
                    editMode={editMode}
                    selectedMembre={selectedMembre}
                />
            )}
        </div>
    );
};

export default AllMembres;
